import * as yup from 'yup'
import { checkLastChar } from './aggregatorSchema'
import { NO_SPECIAL_CHAR, OOS_THRESHOLD_REGEX } from 'utils/constants'
import { storeFormFields as SFF } from 'components/storeManagement/utils'

const stringSchema = yup.string().trim()

const validateCoordinates = (value: any) => {
  if (!value) return true

  const stringValue = typeof value === 'string' ? value : String(value || '')
  const trimmed = stringValue.trim()

  if (trimmed === '') return true
  if (!trimmed.includes(',')) return false

  const parts = trimmed.split(',')
  if (parts.length !== 2) return false

  const [lat, lng] = parts.map((part) => part.trim())
  const latNum = Number(lat)
  const lngNum = Number(lng)

  if (isNaN(latNum) || isNaN(lngNum)) return false
  if (latNum < -90 || latNum > 90) return false
  if (lngNum < -180 || lngNum > 180) return false

  return true
}

export const createStoreSchema = yup
  .object()
  .shape({
    [SFF.STORE_NAME.key]: yup
      .string()
      .trim()
      .required(`${SFF.STORE_NAME.label} is required field`),
    [SFF.STORE_ID.key]: yup
      .string()
      .trim()
      .required(`${SFF.STORE_ID.label} is required field`)
      .test(
        'last-char-check',
        `Please Enter Valid ${SFF.STORE_ID.label}.`,
        (value) => checkLastChar(value),
      )
      .matches(NO_SPECIAL_CHAR, `Please Enter Valid ${SFF.STORE_ID.label}`),
    [SFF.STORE_IS_ACTIVE.key]: yup.boolean(),
    [SFF.STORE_LAST_MILE.key]: yup.object().when(SFF.STORE_IS_THIRD_PARTY.key, {
      is: true,
      then: (schema) => schema.optional().nullable(),
      otherwise: (schema) =>
        schema
          .test(
            'unknown-empty',
            'Unknown group must not contain any aggregators',
            (value) => {
              if (!value) return false
              const val = value as Record<string, any[]>
              const unknownKey = Object.keys(val).find(
                (k) => k.toLowerCase() === 'unknown',
              )
              if (!unknownKey) return true
              return (
                Array.isArray(val[unknownKey]) && val[unknownKey].length === 0
              )
            },
          )
          .required(),
    }),

    [SFF.STORE_INVOICE_PREFIX_CODE.key]: yup
      .string()
      .trim()
      .when(SFF.STORE_IS_THIRD_PARTY.key, {
        is: true,
        then: (schema) => schema.optional().nullable(),
        otherwise: (schema) =>
          schema.required(
            `${SFF.STORE_INVOICE_PREFIX_CODE.label} is required field`,
          ),
      }),

    [SFF.STORE_TALABAT_ID.key]: yup.string().trim().optional().nullable(),
    [SFF.STORE_INSTASHOP_IDENTIFIER.key]: yup
      .string()
      .trim()
      .optional()
      .nullable(),
    [SFF.SAP_STORE_CODE.key]: yup.string().trim().optional().nullable(),
    [SFF.STORE_OOS_THRESHOLD.key]: yup
      .string()
      .trim()
      .required(`${SFF.STORE_OOS_THRESHOLD.label} is required field`)
      .matches(OOS_THRESHOLD_REGEX, {
        message: 'OOS Threshold must be a positive integer',
      }),

    [SFF.STORE_CLUSTER.key]: stringSchema.optional().nullable(),
    [SFF.DIRECT_POSTING_STORE.key]: stringSchema.optional().nullable(),
    [SFF.STORE_EMIRATE.key]: stringSchema.when(SFF.STORE_IS_THIRD_PARTY.key, {
      is: true,
      then: (schema) => schema.optional().nullable(),
      otherwise: (schema) =>
        schema.required(`${SFF.STORE_EMIRATE.label} is required field`),
    }),

    [SFF.STORE_AUTO_ALLOCATE.key]: stringSchema.when(
      SFF.STORE_IS_THIRD_PARTY.key,
      {
        is: true,
        then: (schema) => schema.optional().nullable(),
        otherwise: (schema) =>
          schema.required(`${SFF.STORE_AUTO_ALLOCATE.label} is required field`),
      },
    ),
    [SFF.STORE_INVOICE_LOGO.key]: stringSchema.optional().nullable(),
    [SFF.STORE_INVOICE_SUB_LOGO.key]: stringSchema.optional().nullable(),
    [SFF.STORE_LOGINEXT_TOKEN.key]: stringSchema.nullable(),
    [SFF.STORE_IS_THIRD_PARTY.key]: yup.boolean(),
    [SFF.OTP_PHONE_NUMBERS.key]: stringSchema.optional().nullable(),

    location: yup
      .object()
      .shape({
        coordinates: yup
          .string()
          .optional()
          .nullable()
          .test(
            'valid-coordinates',
            'Invalid coordinates. Format: latitude,longitude (e.g., 12.9716,77.5946)',
            validateCoordinates,
          ),
      })
      .optional(),
  })

  .required()

export const updateStoreSchema = createStoreSchema.shape({
  [SFF.STORE_ID.key]: yup.string().optional(),
})
